import { openDatabase } from '@tunasong/indexeddb-storage'
import { networkBus, shortUuid } from '@tunasong/models'
import invariant from 'tiny-invariant'

/** ISO-8601 datetime string (e.g. '2026-05-25T19:00:00.000Z'). */
type ISODateTime = string

export interface QueuedMutation {
  id: string
  enqueuedAt: ISODateTime
  endpointName: string
  apiName: string
  args: unknown
  /**
   * ETag captured from the entity's RTK Query cache at enqueue time, if any.
   * Sent as `If-Match` on replay so the server can detect intermediate writes
   * (returns 412 → conflict dead-letter). See Pre-flight P.3 finding.
   */
  etag: string | null
  attempts: number
  lastError: string | null
  lastAttemptAt: ISODateTime | null
}

export interface MutationQueue {
  enqueue: (
    m: Pick<QueuedMutation, 'endpointName' | 'apiName' | 'args'> & { etag?: string | null }
  ) => Promise<QueuedMutation>
  remove: (id: string) => Promise<void>
  list: () => Promise<QueuedMutation[]>
  clear: () => Promise<void>
  updateAttempts: (id: string, error: string | null) => Promise<void>
}

const DB_NAME = 'tunasong-mutations'

const publishCount = async (db: ReturnType<typeof openDatabase<QueuedMutation>>) => {
  if (!db) {
    return
  }
  const keys = await db.keys()
  networkBus.emit('mutations-changed', { count: keys.length })
}

/**
 * FIFO comparator for the mutation queue. Primary key is `enqueuedAt`; the
 * unique `id` is a stable tie-break for the rare exactly-equal case (e.g.
 * entries persisted before {@link nextEnqueuedAt} shipped, or a cross-session
 * collision) so `list()` is always a deterministic total order. The old
 * comparator returned 1 on ties, which is not a valid ordering and made
 * `list()` flake (~1-in-4) whenever two mutations shared a millisecond.
 *
 * Note: the id tie-break alone makes `list()` deterministic but NOT FIFO — `id`
 * is a random `shortUuid`, so same-millisecond entries would sort into a random
 * order, not insertion order. True FIFO for same-millisecond bursts comes from
 * {@link nextEnqueuedAt} making `enqueuedAt` strictly monotonic; the tie-break
 * is belt-and-suspenders.
 */
export const byEnqueuedAtThenId = (a: QueuedMutation, b: QueuedMutation): number => {
  if (a.enqueuedAt !== b.enqueuedAt) {
    return a.enqueuedAt < b.enqueuedAt ? -1 : 1
  }
  if (a.id < b.id) {
    return -1
  }
  return a.id > b.id ? 1 : 0
}

/**
 * Monotonic `enqueuedAt` source. `Date.now()` has millisecond resolution, so
 * several mutations enqueued in the same tick (e.g. an offline burst) would
 * share a timestamp and lose their insertion order — the FIFO flake this queue
 * must not have. Track the last issued millisecond across the process and bump
 * by 1ms on a collision so `enqueuedAt` is strictly increasing in enqueue
 * order, then resyncs to wall-clock once real time overtakes the counter.
 * Module-scoped so it holds across all queue instances writing the shared DB.
 */
let lastEnqueuedAtMs = 0
const nextEnqueuedAt = (): ISODateTime => {
  const now = Date.now()
  const ms = now > lastEnqueuedAtMs ? now : lastEnqueuedAtMs + 1
  lastEnqueuedAtMs = ms
  return new Date(ms).toISOString() as ISODateTime
}

export const createMutationQueue = (): MutationQueue => {
  const db = openDatabase<QueuedMutation>(DB_NAME)

  const list = async (): Promise<QueuedMutation[]> => {
    if (!db) {
      return []
    }
    const entries = await db.values()
    return entries.sort(byEnqueuedAtThenId)
  }

  const enqueue: MutationQueue['enqueue'] = async m => {
    invariant(db, 'mutationQueue: IndexedDB unavailable (server-side or no window)')
    const entry: QueuedMutation = {
      id: shortUuid(),
      enqueuedAt: nextEnqueuedAt(),
      endpointName: m.endpointName,
      apiName: m.apiName,
      args: m.args,
      etag: m.etag ?? null,
      attempts: 0,
      lastError: null,
      lastAttemptAt: null,
    }
    await db.set(entry.id, entry)
    await publishCount(db)
    return entry
  }

  const remove = async (id: string) => {
    if (!db) {
      return
    }
    await db.delete(id)
    await publishCount(db)
  }

  const clear = async () => {
    if (!db) {
      return
    }
    await db.clear()
    await publishCount(db)
  }

  const updateAttempts: MutationQueue['updateAttempts'] = async (id, error) => {
    if (!db) {
      return
    }
    const entry = await db.get(id)
    if (!entry) {
      return
    }
    await db.set(id, {
      ...entry,
      attempts: entry.attempts + 1,
      lastError: error,
      lastAttemptAt: new Date().toISOString() as ISODateTime,
    })
  }

  return { enqueue, remove, list, clear, updateAttempts }
}

/**
 * Per-endpoint replay-aware classification of a server response on REPLAY.
 * Returns 'success' when the response indicates the desired state has been
 * reached (entity was already created, already updated, already deleted —
 * see Pre-flight P.3 findings).
 */
export type ReplayClassification = 'success' | 'conflict' | 'failure'

export const classifyReplayResult = (
  endpointName: string,
  status: number | string | undefined,
  errorBody: unknown
): ReplayClassification => {
  // deleteEntity replay: entity already gone → success
  if (endpointName === 'deleteEntity' && status === 404) {
    return 'success'
  }
  // createEntity replay: duplicate id (DynamoDB ConditionalCheckFailed) → success
  if (
    endpointName === 'createEntity' &&
    status === 500 &&
    JSON.stringify(errorBody).includes('ConditionalCheckFailed')
  ) {
    return 'success'
  }
  // updateEntity / setEntityTags / setEntityAcls with If-Match → 412 means
  // intermediate writes happened; surface as a conflict for dead-letter UI.
  if (status === 412) {
    return 'conflict'
  }
  return 'failure'
}

export interface ReplayOpts {
  queue: MutationQueue
  dispatch: (m: QueuedMutation) => Promise<unknown>
  retryDelayMs?: number
}

const MAX_ATTEMPTS = 5

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export const replayMutationQueue = async (opts: ReplayOpts): Promise<void> => {
  const { queue, dispatch, retryDelayMs = 2000 } = opts
  const items = await queue.list()
  for (const item of items) {
    let attempt = item.attempts
    while (attempt < MAX_ATTEMPTS) {
      try {
        await dispatch(item)
        await queue.remove(item.id)
        break
      } catch (e) {
        const status = (e as { status?: number }).status
        const body = (e as { data?: unknown }).data
        if (status === 401) {
          // Halt replay so auth refresh can run
          return
        }
        // Replay-aware: per Pre-flight P.3, some statuses on a replayed
        // mutation indicate the desired state has already been reached.
        const classification = classifyReplayResult(item.endpointName, status, body)
        if (classification === 'success') {
          await queue.remove(item.id)
          break
        }
        if (classification === 'conflict') {
          await queue.updateAttempts(item.id, `CONFLICT: ${e instanceof Error ? e.message : String(e)}`)
          break
        }
        attempt += 1
        await queue.updateAttempts(item.id, e instanceof Error ? e.message : String(e))
        if (attempt < MAX_ATTEMPTS && retryDelayMs > 0) {
          await delay(retryDelayMs * 2 ** (attempt - 1))
        }
      }
    }
  }
}
